import { useEffect, useRef } from 'react';
import { i18next } from '@frontend/i18n';
import { useQueueStore } from '@frontend/queue';
import { useAppScopeStore } from '@frontend/scope';
import { useRouter } from '@frontend/weave-router';
import { useAlert } from '@frontend/design-system';

const voicemailHandler = (params: Record<string, string>, allowedLocations: string[]) => {
  const voicemailId = params.voicemailId;
  const locationId = params.locationId;
  const enqueue = useQueueStore.getState().enqueue;

  const locationAllowed = locationId && allowedLocations.includes(locationId);

  if (voicemailId && locationAllowed) {
    const eventId = enqueue('voicemail-details', {
      payload: {
        type: 'query-then-open-detached',
        voicemailId,
        locationId,
      },
      waitForHandler: true,
    });

    return {
      eventId,
      message: '',
    };
  } else if (voicemailId && !locationAllowed) {
    return {
      eventId: null,
      message: i18next.t('Shortlink not allowed for this location.'),
    };
  } else {
    return {
      eventId: null,
      message: i18next.t('Invalid shortlink.'),
    };
  }
};

const callHistoryHandler = (params: Record<string, string>, allowedLocations: string[]) => {
  const channelId = params.channelId;
  const locationId = params.locationId;
  const enqueue = useQueueStore.getState().enqueue;

  const locationAllowed = locationId && allowedLocations.includes(locationId);

  if (channelId && locationAllowed) {
    const eventId = enqueue('call-insights', {
      payload: {
        channelId,
        locationId,
      },
      waitForHandler: true,
    });
    return {
      eventId,
      message: '',
    };
  } else if (channelId && !locationAllowed) {
    return {
      eventId: null,
      message: i18next.t('Shortlink not allowed for this location.'),
    };
  } else {
    return {
      eventId: null,
      message: i18next.t('Invalid shortlink.'),
    };
  }
};

const isValidShortlinkUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);

    const allowedHosts = ['getweave.com', 'weavedev.net', 'localhost'];
    if (!allowedHosts.includes(url.hostname)) {
      return false;
    }

    if (url.protocol !== 'https:' && url.hostname !== 'localhost') {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const sanitizeParams = (params: URLSearchParams): Record<string, string> => {
  const sanitized: Record<string, string> = {};
  const allowedParams = ['type', 'voicemailId', 'locationId', 'channelId'];

  for (const [key, value] of params.entries()) {
    // Only allow expected parameters
    if (!allowedParams.includes(key)) continue;

    const sanitizedValue = value.replace(/[<>&"']/g, '');
    if (sanitizedValue.length > 100) continue; // Prevent excessively long values

    sanitized[key] = sanitizedValue;
  }

  return sanitized;
};

export const useHandleShortlinks = () => {
  const { selectedLocationIds, forcedGroupId } = useAppScopeStore();
  const topBarRef = useRef<HTMLDivElement>(null);

  const alerts = useAlert();
  const allowedLocations = selectedLocationIds.concat([forcedGroupId].filter(Boolean));

  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData('text/plain');
      if (!pastedText) return;

      if (!isValidShortlinkUrl(pastedText)) {
        alerts.error(i18next.t('Invalid or untrusted URL'));
        return;
      }

      const url = new URL(pastedText);
      const params = new URLSearchParams(url.search);
      const sanitizedParams = sanitizeParams(params);

      const type = sanitizedParams.type;
      if (!type) return;

      if (type === 'voicemail') voicemailHandler(sanitizedParams, allowedLocations);
      else if (type === 'callHistory') callHistoryHandler(sanitizedParams, allowedLocations);
      else alerts.error(i18next.t('Unsupported shortlink type.'));
    };

    const currentRef = topBarRef.current;
    currentRef?.addEventListener('paste', handler);
    return () => currentRef?.removeEventListener('paste', handler);
  }, [alerts, allowedLocations]);

  return { topBarRef };
};

export const useHandleShortlinksOnAppLoad = () => {
  const router = useRouter();
  const { selectedLocationIds, forcedGroupId } = useAppScopeStore();
  const alerts = useAlert();
  const allowedLocations = selectedLocationIds.concat([forcedGroupId].filter(Boolean));

  const isHandled = useRef(false);

  useEffect(() => {
    const onLoadHandler = () => {
      const params = new URLSearchParams(window.location.search);

      const sanitizedParams = sanitizeParams(params);

      const type = sanitizedParams.type;
      if (!type) return;

      if (type === 'voicemail') voicemailHandler(sanitizedParams, allowedLocations);
      else if (type === 'callHistory') callHistoryHandler(sanitizedParams, allowedLocations);
      else alerts.error(i18next.t('Unsupported shortlink type.'));

      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    };

    if (!isHandled.current) {
      isHandled.current = true;
      onLoadHandler();
    }

    const unsubscribe = router.subscribe('onLoad', () => {
      onLoadHandler();
    });

    return unsubscribe;
  }, [alerts, allowedLocations, router]);
};
